import { useMutation, useQuery } from "@tanstack/react-query";
import { GetPayloadPropsExternalTraining, PayloadPropExternalsTraining } from "../routes/Profile/ExternalTrainingAddSkillbook/AddSkillBook.types";
import { fetcherFactory } from "../services";
import { useAuthStore } from "../stores";
import { getEnvironmentVariables } from "../utils/general";

const { basePath } = getEnvironmentVariables();

interface UseOptionsQueryParams {
  enabled?: boolean;
  queryKey?: string[];
  onSuccess?: (data: { output: [] }) => void;
  onError?: (error: string) => void;
  onSettled?: (data: { output: [] }, error: string) => void;
  knowledgeAreaId?: string | number;
}
interface UseExternalRequestQueryParams {
  enabled?: boolean;
  onSuccess?: (data: { content: GetPayloadPropsExternalTraining[] }) => void;
  onError?: (error: string) => void;
  queryParams: {
    page?: number;
    size?: number;
    learnerId: string | number;
    corporateId: string | number;
    name?: string;
    surname?: string;
    title?: string;
    durationMinutes?: number;
    startDate?: string;
    endDate?: string;
    courseType?: number;
    certificateStartValidityDate?: string;
    certificateEndValidityDate?: string;
    code?: string;
    knowledgeAreaId?: number;
    knowledgeDomainId?: number;
    capabilityId?: number;
    year?: number;
    status?: string;
  };
  queryKey: string[];
}

export function useKnowledgeAreasOptionsQuery({
  enabled = true,
  onSuccess,
  onError
}: UseOptionsQueryParams) {

  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const corporateInfo = useAuthStore(state => state.corporateInfo);
  const fetcher = fetcherFactory();

  const uri = `/skills/${corporateInfo!.id}/knowledge-areas`;

  return useQuery(
    [uri],
    () => fetcher(
      `${basePath}${uri}`,
      {
        headers: {
          "authorization": idToken,
          "x-ada-session-token": sessionToken
        }
      }
    ).then((res) => {
      return res.json();
    }),
    {
      enabled: Boolean(enabled && sessionToken && idToken) && !isSeedingSession,
      onSuccess,
      onError
    }
  );
}

export function useKnowledgeDomainsOptionsQuery({
  knowledgeAreaId,
  enabled = true,
  onSuccess,
  onError
}: UseOptionsQueryParams) {

  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const corporateInfo = useAuthStore(state => state.corporateInfo);
  const fetcher = fetcherFactory();

  const uri = `/skills/${corporateInfo!.id}/knowledge-areas/${knowledgeAreaId}/domains`;

  return useQuery(
    [uri],
    () => fetcher(
      `${basePath}${uri}`,
      {
        headers: {
          "authorization": idToken,
          "x-ada-session-token": sessionToken
        }
      }
    ).then((res) => {
      return res.json();
    })
      .catch((error) => {
        onError;
        throw error;
      }),
    {
      enabled: Boolean(enabled && sessionToken && idToken) && !isSeedingSession,
      onSuccess
    }
  );
}

export function useCapabilitiesOptionsQuery({
  enabled = true,
  onSuccess,
  onError
}: UseOptionsQueryParams) {

  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const corporateInfo = useAuthStore(state => state.corporateInfo);
  const fetcher = fetcherFactory();

  const uri = `/skills/${corporateInfo!.id}/capabilities`;

  return useQuery(
    [uri],
    () => fetcher(
      `${basePath}${uri}`,
      {
        headers: {
          "authorization": idToken,
          "x-ada-session-token": sessionToken
        }
      }
    ).then((res) => {
      return res.json();
    })
      .catch((error) => {
        onError;
        throw error;
      }),
    {
      enabled: Boolean(enabled && sessionToken && idToken) && !isSeedingSession,
      onSuccess,
      onError
    });
}

export function useCourseTypesOptionsQuery({
  enabled = true,
  onSuccess,
  onError
}: UseOptionsQueryParams) {

  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const fetcher = fetcherFactory();

  const uri = "/certificates/external/course-type";

  return useQuery(
    [uri],
    () => fetcher(
      `${basePath}${uri}`,
      {
        headers: {
          "authorization": idToken,
          "x-ada-session-token": sessionToken
        }
      }
    ).then((res) => {
      return res.json();
    })
      .catch((error) => {
        console.log("error", error);
        onError;
        throw error;
      }),
    {
      enabled: Boolean(enabled && sessionToken && idToken) && !isSeedingSession,
      onSuccess,
      onError
    });
}

export function useExternalTrainingQuery({
  enabled = true,
  onSuccess,
  onError,
  queryParams,
  queryKey
}: UseExternalRequestQueryParams) {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();

  const queryString = new URLSearchParams(
    Object.entries(queryParams).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        acc[key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>)
  ).toString();

  const uri = `/external-trainings/training-requests/detail?${queryString}`;

  return useQuery(
    ["external-training-requests", "pending",...queryKey],
    () => fetcher(`${basePath}${uri}`,
      {
        headers: {
          authorization: idToken,
          "x-ada-session-token": sessionToken
        }
      }
    ).then(res => res.json()),
    {
      enabled: Boolean(enabled && sessionToken && idToken),
      onSuccess,
      onError
    }
  );
}

export function useExternalTrainingQueryApproved({
  enabled = true,
  onSuccess,
  onError,
  queryParams,
  queryKey
}: UseExternalRequestQueryParams) {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();

  const queryString = new URLSearchParams(
    Object.entries(queryParams).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        acc[key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>)
  ).toString();

  const uri = `/external-trainings/training-requests/detail?${queryString}`;

  return useQuery(
    ["external-training-requests", "approved", ...queryKey],
    () => fetcher(`${basePath}${uri}`,
      {
        headers: {
          authorization: idToken,
          "x-ada-session-token": sessionToken
        }
      }
    ).then(res => res.json()),
    {
      enabled: Boolean(enabled && sessionToken && idToken),
      onSuccess,
      onError
    }
  );
}

export function useExternalTrainingQueryRejected({
  enabled = true,
  onSuccess,
  onError,
  queryParams,
  queryKey
}: UseExternalRequestQueryParams) {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();

  const queryString = new URLSearchParams(
    Object.entries(queryParams).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        acc[key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>)
  ).toString();

  const uri = `/external-trainings/training-requests/detail?${queryString}`;

  return useQuery(
    ["external-training-requests", "rejected", ...queryKey],
    () => fetcher(`${basePath}${uri}`,
      {
        headers: {
          authorization: idToken,
          "x-ada-session-token": sessionToken
        }
      }
    ).then(res => res.json()),
    {
      enabled: Boolean(enabled && sessionToken && idToken),
      onSuccess,
      onError
    }
  );
}


export function usePostExternalTraining({
  onSuccess,
  onError
}: UseOptionsQueryParams) {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();
  const uri = "/external-trainings";

  return useMutation(
    async (data: PayloadPropExternalsTraining ) => {
      console.log("Invio dati a:", `${ basePath }${ uri }`);
      console.log("Dati inviati:", data);

      //const payload = {
      //  ...data,
      //  trainingMode: data.trainingMode === "" ? null : data.trainingMode
      //};

      const formData = new FormData();

      (data.attachments ?? []).forEach((file: string) => formData.append("attachments", file));
      formData.append("data", JSON.stringify(data.data));

      console.log("formData", formData);

      try {
        const response = await fetcher(
          `${ basePath }${ uri }`,
          {
            method: "POST",
            headers: {
              "authorization": idToken,
              "x-ada-session-token": sessionToken
            },
            body: formData
          }
        );
        return response;
      } catch (error) {
        console.error("Errore durante l'invio:", error);
        throw error;
      }
    },
    {
      onSuccess: (data) => {
        console.log("Successo dalla mutazione:", data);
        if (onSuccess) onSuccess(data as unknown as  { output: [] });
      },
      onError: (error) => {
        console.error("Errore dalla mutazione:", error);
        if (onError) onError(error as string);
      }
    }
  );
}
